// "Download PDF report" — collects the tool's visible result rows and
// returns a branded one-page PDF from the backend. Optional email capture:
// visitors can also receive the report link by email (consent recorded).
import { useState } from "react";
import { Download, Mail } from "lucide-react";
import { track, toolParams } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function collectRows() {
  return [...document.querySelectorAll('[data-testid="tool-body"] [data-report-row]')]
    .map((el) => {
      const spans = el.querySelectorAll(":scope > span");
      if (spans.length < 2) return null;
      return { label: spans[0].textContent.trim(), value: spans[spans.length - 1].textContent.trim() };
    })
    .filter((r) => r && r.label && r.value && r.value !== "—")
    .slice(0, 30);
}

async function requestReport(tool, rows, email, marketingConsent) {
  const res = await fetch(`${API}/api/tools/report`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      toolId: tool.id,
      toolName: tool.name,
      results: rows,
      note: "Computed with your inputs using the fixed methodology shown on the tool page.",
      ...(email ? { email, marketingConsent } : {}),
    }),
  });
  if (!res.ok) throw new Error(`report ${res.status}`);
  return res.blob();
}

function saveBlob(blob, tool) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `blezzings-${tool.slug}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ToolReportButton({ tool, testid = "tool-report-btn" }) {
  const [state, setState] = useState("idle"); // idle | working | done | error
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [mailState, setMailState] = useState("idle"); // idle | working | sent | error | invalid

  const download = async () => {
    setState("working");
    try {
      const rows = collectRows();
      if (!rows.length) {
        setState("error");
        setTimeout(() => setState("idle"), 3000);
        return;
      }
      saveBlob(await requestReport(tool, rows), tool);
      track("report_download", toolParams(tool));
      setState("done");
      setTimeout(() => setState("idle"), 3000);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setMailState("invalid");
      return;
    }
    setMailState("working");
    try {
      const rows = collectRows();
      if (!rows.length) throw new Error("empty");
      const blob = await requestReport(tool, rows, email.trim(), consent);
      saveBlob(blob, tool); // visitor also gets the file locally
      track("report_email_optin", { ...toolParams(tool), marketing_consent: !!consent });
      setMailState("sent");
    } catch {
      setMailState("error");
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={download}
          disabled={state === "working"}
          data-testid={testid}
          className="inline-flex items-center gap-2 h-11 px-5 border border-black/20 text-black text-sm font-medium hover:border-black transition-colors disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {state === "working" ? "Preparing PDF…" : state === "done" ? "Downloaded" : state === "error" ? "No results yet — run the tool" : "Download PDF report"}
        </button>
        <button
          type="button"
          onClick={() => setEmailOpen((v) => !v)}
          data-testid="tool-report-email-toggle"
          aria-expanded={emailOpen}
          className="inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4 hover:text-zinc-600"
        >
          <Mail className="h-4 w-4" /> Email me this report
        </button>
        <span className="text-xs text-zinc-400">Your numbers stay in your browser until you choose to download or share.</span>
      </div>
      {emailOpen && (
        <form onSubmit={sendEmail} className="mt-4 max-w-lg border border-zinc-200 bg-white p-5" data-testid="tool-report-email-form">
          {mailState === "sent" ? (
            <p className="text-sm text-zinc-700" data-testid="tool-report-email-success">
              On its way to <strong>{email}</strong> — check your inbox for the download link. The PDF has also been downloaded here.
            </p>
          ) : (
            <>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Work email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1.5 w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-black"
                  data-testid="tool-report-email-input"
                />
              </label>
              {mailState === "invalid" && <p className="mt-2 text-xs text-red-700" data-testid="tool-report-email-invalid">Please enter a valid email address.</p>}
              {mailState === "error" && <p className="mt-2 text-xs text-red-700" data-testid="tool-report-email-error">Couldn't send just now — please try the direct download instead.</p>}
              <label className="mt-3 flex items-start gap-2.5 text-xs text-zinc-600 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-black" data-testid="tool-report-consent" />
                Also send me occasional insights from BLEZZINGS (optional — your report arrives either way).
              </label>
              <button
                type="submit"
                disabled={mailState === "working"}
                data-testid="tool-report-email-submit"
                className="mt-4 h-10 px-5 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {mailState === "working" ? "Sending…" : "Email the report"}
              </button>
            </>
          )}
        </form>
      )}
    </div>
  );
}
